import * as React from 'react';
import {Helmet} from 'react-helmet';
import {ChoiceList, Heading, TextContainer} from '@shopify/polaris';
import {
  AppLayout,
  FiltersContainer,
  ServiceCard,
  PageHeader,
  Breadcrumbs,
} from 'sections/services-marketplace/experts/components';
import {TaxonomyNode, Service} from 'sections/types';
import {
  fetchTaxonomy,
  Routes,
} from 'sections/services-marketplace/experts/utilities';
import {withI18n, WithI18nProps} from '@shopify/react-i18n';
import i18nArgs from './locales';

import * as styles from './ServiceIndex.scss';
import {producePageViewMonorailEvent} from '../../utilities/monorail';
import {MonorailPageIndicators} from '../../utilities/constants';

export interface OwnProps {
  taxonomyNode: TaxonomyNode;
}

type CombinedProps = OwnProps & WithI18nProps;

interface State {
  service: string[];
  taxonomyNode: TaxonomyNode;
}

type ServiceChoice = {
  label: string;
  value: string;
};

const taxonomy = fetchTaxonomy();

class ServiceIndex extends React.Component<CombinedProps, State> {
  static getDerivedStateFromProps(props: CombinedProps, state: State) {
    if (state.taxonomyNode !== props.taxonomyNode) {
      return {
        service: [''],
        taxonomyNode: props.taxonomyNode,
      };
    }
    return null;
  }

  state: State = {
    service: [''],
    taxonomyNode: this.props.taxonomyNode,
  };

  taxonomy = fetchTaxonomy();

  onFilterChange = (service: string[]): void => {
    this.setState({service});
  };

  componentDidMount() {
    producePageViewMonorailEvent({
      pageIndicator: MonorailPageIndicators.serviceIndex,
      serviceHandle: this.props.taxonomyNode.service.handle,
    });
  }

  componentDidUpdate(prevProps: CombinedProps) {
    const {taxonomyNode} = this.state;

    if (taxonomyNode !== prevProps.taxonomyNode) {
      producePageViewMonorailEvent({
        pageIndicator: MonorailPageIndicators.serviceIndex,
        serviceHandle: this.props.taxonomyNode.service.handle,
      });
    }
  }

  render() {
    const {
      taxonomyNode: {service},
    } = this.props;
    const ancestors = taxonomy.getAncestors(service.handle);
    const parentCategory = ancestors.length ? ancestors[0] : service;

    return (
      <AppLayout>
        {this.renderHelmet(service)}
        <AppLayout.Container>
          <AppLayout.Section fill>
            <PageHeader
              title={service.label}
              description={service.description}
              image={Routes.imagePath(`svg/${parentCategory.handle}.svg`)}
              actions={<Breadcrumbs services={ancestors} />}
            />
          </AppLayout.Section>
        </AppLayout.Container>
        <AppLayout.Container>{this.renderResults()}</AppLayout.Container>
      </AppLayout>
    );
  }

  renderHelmet = (service: Service) => {
    const {i18n} = this.props;
    const canonicalPath = `https://experts.shopify.com${Routes.servicePath(
      this.taxonomy,
      service.handle
    )}`;
    return (
      <Helmet>
        <title>
          {i18n.translate('ServiceIndex.Meta.categoryTitle', {
            serviceLabel: service.shortLabel.toLowerCase(),
          })}
        </title>
        <meta
          name="description"
          content={i18n.translate('ServiceIndex.Meta.categoryDescription', {
            serviceLabel: service.shortLabel.toLowerCase(),
          })}
        />
        <link rel="canonical" href={canonicalPath} />
      </Helmet>
    );
  };

  renderFilters(serviceFilters: ServiceChoice[]) {
    const {
      taxonomyNode: {service},
    } = this.props;

    return serviceFilters.length > 1 ? (
      <AppLayout.Section size="OneFifth">
        <FiltersContainer>
          <TextContainer spacing="tight">
            <Heading>{service.shortLabel}</Heading>
            <ChoiceList
              choices={serviceFilters}
              selected={this.state.service}
              onChange={this.onFilterChange}
            />
          </TextContainer>
        </FiltersContainer>
      </AppLayout.Section>
    ) : null;
  }

  renderResults() {
    const {taxonomyNode} = this.props;
    const results = this.taxonomy
      .getFlattenedTaxonomyNode(taxonomyNode)
      .children.filter((item: any) => !item.children);

    return (
      <div className={styles.ResultsContainer}>
        <div className={styles.Results}>
          {results.map(item => {
            const image = Routes.imagePath(
              `services/${item.service.handle}.jpg`
            );
            const image2x = Routes.imagePath(
              `services/${item.service.handle}@2x.jpg`
            );
            return (
              <div key={item.service.handle}>
                <ServiceCard
                  service={item.service}
                  imageSrc={image}
                  imageSrcSet={`${image} 1x, ${image2x} 2x`}
                />
              </div>
            );
          })}
        </div>
      </div>
    );
  }
}

export default withI18n(i18nArgs)(ServiceIndex);
